import type { CurrencyDefinition } from '@/types/domain';

/**
 * Multi-currency module.
 *
 * The base currency is TRY. Foreign currencies are NOT assumed: every entry
 * starts inactive with a `null` rate. An admin enables currencies and enters
 * rates later (manually or through a future rates API). Nothing here invents
 * a live rate.
 */

export const BASE_CURRENCY = 'TRY';

export const CURRENCIES: CurrencyDefinition[] = [
  { code: 'TRY', symbol: '₺', rate: 1, isBase: true, active: true },
  { code: 'USD', symbol: '$', rate: null, isBase: false, active: false },
  { code: 'EUR', symbol: '€', rate: null, isBase: false, active: false },
  { code: 'GBP', symbol: '£', rate: null, isBase: false, active: false },
  { code: 'AED', symbol: 'AED', rate: null, isBase: false, active: false },
  { code: 'RUB', symbol: '₽', rate: null, isBase: false, active: false },
];

export function getActiveCurrencies(): CurrencyDefinition[] {
  return CURRENCIES.filter((c) => c.active);
}

export function getCurrency(code: string): CurrencyDefinition | undefined {
  return CURRENCIES.find((c) => c.code === code);
}

/**
 * Converts an amount expressed in the base currency into the target currency.
 * Returns `null` when the rate is not configured, so callers can show a
 * "quote" state instead of an incorrect number.
 */
export function convertFromBase(amountBase: number, targetCode: string): number | null {
  const target = getCurrency(targetCode);
  if (!target || target.rate == null) return null;
  return amountBase * target.rate;
}

export function formatPrice(
  amount: number | null,
  currencyCode: string,
  locale = 'tr-TR',
): string | null {
  if (amount == null) return null;
  const currency = getCurrency(currencyCode);
  if (!currency) return null;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${currency.symbol}${amount.toLocaleString(locale)}`;
  }
}